import React, { useEffect, useState } from 'react';
import { councilPortal } from '../services/api';

const STATUS_COLORS = {
  open:        'bg-blue-100 text-blue-800',
  scheduled:   'bg-purple-100 text-purple-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  completed:   'bg-green-100 text-green-800',
  cancelled:   'bg-gray-100 text-gray-600',
};

const PRIORITY_COLORS = {
  emergency: 'bg-red-100 text-red-800',
  urgent:    'bg-orange-100 text-orange-800',
  high:      'bg-yellow-100 text-yellow-800',
  medium:    'bg-blue-100 text-blue-800',
  low:       'bg-gray-100 text-gray-600',
};

export default function CouncilWorkOrders() {
  const [workorders, setWorkorders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    councilPortal.workorders()
      .then((res) => setWorkorders(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
    </div>
  );

  const filtered = filter === 'all'
    ? workorders
    : workorders.filter((w) => w.status === filter);

  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  const fmtMoney = (v) => v != null ? `$${Number(v).toLocaleString()}` : '—';

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Work Orders</h1>
        <p className="text-gray-500 text-sm mt-1">Read-only view of all maintenance work orders.</p>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {['all', 'open', 'scheduled', 'in_progress', 'completed'].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === s
                ? 'bg-emerald-600 text-white'
                : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}>
            {s === 'all' ? 'All' : s.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            <span className="ml-1.5 text-xs opacity-70">
              ({s === 'all' ? workorders.length : workorders.filter((w) => w.status === s).length})
            </span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
          No work orders found.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Work Order</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Priority</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Quoted</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Approved</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((w) => (
                <tr key={w.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="font-medium text-gray-900">{w.title}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[w.status] || 'bg-gray-100 text-gray-600'}`}>
                      {w.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {w.priority && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_COLORS[w.priority] || 'bg-gray-100 text-gray-600'}`}>
                        {w.priority}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{fmtMoney(w.quoted_amount)}</td>
                  <td className="px-4 py-3 text-gray-600">{fmtMoney(w.approved_amount)}</td>
                  <td className="px-4 py-3 text-gray-500">
                    {w.completed_date ? fmtDate(w.completed_date) : fmtDate(w.scheduled_date)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
